import { createApi } from "@reduxjs/toolkit/query/react";

import {
  API_COMPANY_APPROVE_ASSET_REQUEST,
  API_COMPANY_ASSET_HISTORY_BY_EMPLOYEE,
  API_COMPANY_CREATE_CONSUMABLE_ASSET,
  API_COMPANY_CREATE_NEW_ASSET,
  API_COMPANY_DELETE_ASSET,
  API_COMPANY_DELETE_ASSET_IMAGE,
  API_COMPANY_DELETE_CONSUMABLE_ASSET,
  API_COMPANY_GET_ALL_ASSET_BRANDS,
  API_COMPANY_GET_ALL_ASSET_REQUESTS,
  API_COMPANY_GET_ASSET_REQUEST_STATS,
  API_COMPANY_GET_ALL_ASSET_TYPES,
  API_COMPANY_GET_ALL_CONSUMABLE_ASSETS,
  API_COMPANY_GET_ASSETS_BY_TYPE,
  API_COMPANY_GET_ASSETS_IMAGES,
  API_COMPANY_GET_ASSETS_INVENTORY,
  API_COMPANY_GET_ASSET_DETAILS_BY_ID,
  API_COMPANY_GET_ASSET_REQUEST_DETAILS_BY_ID,
  API_COMPANY_GET_ASSET_STATS,
  API_COMPANY_GET_CONSUMABLE_ASSET_BY_ID,
  API_COMPANY_GET_CONSUMABLE_ASSET_CATEGORY,
  API_COMPANY_GET_CONSUMABLE_UNIT_TYPES,
  API_COMPANY_GET_CONSUMABLE_ASSIGNEES,
  API_COMPANY_REASSIGN_ASSET,
  API_COMPANY_REJECT_ASSET_REQUEST,
  API_COMPANY_RESTORE_ASSET,
  API_COMPANY_RETIRE_ASSET,
  API_COMPANY_UNASSIGN_ASSET,
  API_COMPANY_UPDATE_ASSET,
  API_COMPANY_UPDATE_ASSET_PRIMARY_IMAGE,
  API_COMPANY_UPDATE_CONSUMABLE_ASSET,
} from "@/constants/apiEndpoints";
import type {
  IAssetType,
  IAssetFile,
  ICompanyGetAssetsInventoryParams,
  ICompanySingleInventoryEntry,
  IConsumableAsset,
  IConsumableUnitType,
  ICreateConsumableAssetPayload,
  ISingleAsset,
  IAssetStatsResponse,
  IAssetRequestStats,
  ISingleAssetRequest,
  IConsumableAssetCategory,
  IConsumableAssignees,
  IAssetRequestData,
} from "@/types/(company)/assets.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type {
  IApiGeneralParams,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";
import type { IAssetBrand } from "@/types/(company)/assetBrands.interface";

export const companyAssetsApi = createApi({
  reducerPath: "companyAssetsApi",
  baseQuery: dynamicBaseQuery,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "companyGetAssetStats",
    "companyGetAllAssetTypes",
    "companyGetAllAssetBrands",
    "companyGetAssetsInventory",
    "companyGetSingleAssetTypeList",
    "companyGetAssetDetailsById",
    "companyGetAssetsImages",
    "companyGetAllConsumableAssets",
    "companyGetAllAssetRequests",
    "companyAssetRequestStats",
    "companyGetEmployeeAssignedAssets",
  ],
  endpoints: (builder) => ({
    companyGetAssetStats: builder.query<
      IApiResponse<IAssetStatsResponse>,
      void
    >({
      query: () => ({
        url: API_COMPANY_GET_ASSET_STATS,
        method: "GET",
      }),
      providesTags: ["companyGetAssetStats"],
    }),
    companyGetAllAssetTypes: builder.query<
      IApiPaginatedResponse<Array<IAssetType>>,
      { page?: number; limit: number; search?: string }
    >({
      query: (params) => ({
        url: API_COMPANY_GET_ALL_ASSET_TYPES,
        params,
      }),
      providesTags: ["companyGetAllAssetTypes"],
    }),
    companyGetAllAssetBrands: builder.query<
      IApiPaginatedResponse<Array<IAssetBrand>>,
      { page?: number; limit?: number; search?: string; isActive?: boolean }
    >({
      query: (params) => ({
        url: API_COMPANY_GET_ALL_ASSET_BRANDS,
        params,
      }),
      providesTags: ["companyGetAllAssetBrands"],
    }),
    companyGetAssetsInventory: builder.query<
      IApiPaginatedResponse<ICompanySingleInventoryEntry[]>,
      ICompanyGetAssetsInventoryParams
    >({
      query: (params) => ({
        url: API_COMPANY_GET_ASSETS_INVENTORY,
        params,
      }),
      providesTags: ["companyGetAssetsInventory"],
    }),
    companyGetSingleAssetTypeList: builder.query<
      IApiPaginatedResponse<ISingleAsset[]>,
      {
        assetTypeId: number;
        filterOnlyAvailable?: boolean;
        limit?: number;
        status?: string;
        search?: string;
        page?: number;
        branchId?: number;
      }
    >({
      query: (params) => ({
        url: API_COMPANY_GET_ASSETS_BY_TYPE,
        params,
      }),
      providesTags: ["companyGetSingleAssetTypeList"],
    }),
    companyGetEmployeeAssignedAssets: builder.query<
      IApiPaginatedResponse<ISingleAsset[]>,
      { assignedTo: number }
    >({
      query: (params) => ({
        url: API_COMPANY_GET_ASSETS_BY_TYPE,
        params: { ...params, limit: 100, page: 1 },
      }),
      providesTags: ["companyGetEmployeeAssignedAssets"],
    }),
    companyGetEmployeeAssetHistory: builder.query<IApiResponse<any[]>, { userId: number }>({
      query: ({ userId }) => ({ url: API_COMPANY_ASSET_HISTORY_BY_EMPLOYEE(userId) }),
      providesTags: ["companyGetEmployeeAssignedAssets"],
    }),
    companyCreateNewAsset: builder.mutation<
      IApiResponse<ISingleAsset>,
      FormData
    >({
      query: (body) => ({
        url: API_COMPANY_CREATE_NEW_ASSET,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        "companyGetSingleAssetTypeList",
        "companyGetAssetsInventory",
      ],
    }),
    companyUpdateAsset: builder.mutation<
      IApiResponse<ISingleAsset>,
      { assetId: number; payload: FormData }
    >({
      query: ({ assetId, payload }) => ({
        url: API_COMPANY_UPDATE_ASSET(assetId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["companyGetSingleAssetTypeList"],
    }),
    companyGetAssetDetailsById: builder.query<
      IApiResponse<ISingleAsset>,
      { assetId: number }
    >({
      query: (params) => ({
        url: API_COMPANY_GET_ASSET_DETAILS_BY_ID(params.assetId),
        method: "GET",
      }),
      providesTags: ["companyGetAssetDetailsById"],
    }),
    companyGetAssetsImages: builder.query<
      IApiResponse<IAssetFile[]>,
      { assetId: number }
    >({
      query: (params) => ({
        url: API_COMPANY_GET_ASSETS_IMAGES(params.assetId),
        method: "GET",
      }),
      providesTags: ["companyGetAssetsImages"],
    }),
    companyUpdateAssetPrimaryImage: builder.mutation<
      IApiResponse<null>,
      { assetId: number; newPrimaryId: number }
    >({
      query: ({ assetId, newPrimaryId }) => ({
        url: API_COMPANY_UPDATE_ASSET_PRIMARY_IMAGE(assetId),
        body: { newPrimaryId },
        method: "PUT",
      }),
      invalidatesTags: ["companyGetAssetsImages"],
    }),
    companyUnassignAsset: builder.mutation<
      IApiResponse<null>,
      { assetId: number; payload: FormData }
    >({
      query: ({ assetId, payload }) => ({
        method: "PUT",
        url: API_COMPANY_UNASSIGN_ASSET(assetId),
        body: payload,
      }),
      invalidatesTags: [
        "companyGetSingleAssetTypeList",
        "companyGetAssetDetailsById",
        "companyGetEmployeeAssignedAssets",
      ],
    }),
    companyRetireAsset: builder.mutation<
      IApiResponse<null>,
      { assetId: number; payload: FormData }
    >({
      query: ({ assetId, payload }) => ({
        url: API_COMPANY_RETIRE_ASSET(assetId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [
        "companyGetSingleAssetTypeList",
        "companyGetAssetStats",
        "companyGetAssetDetailsById",
      ],
    }),
    companyRestoreAsset: builder.mutation<
      IApiResponse<null>,
      { assetId: number; payload: FormData }
    >({
      query: ({ assetId, payload }) => ({
        url: API_COMPANY_RESTORE_ASSET(assetId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [
        "companyGetSingleAssetTypeList",
        "companyGetAssetStats",
        "companyGetAssetDetailsById",
      ],
    }),
    companyAssignAsset: builder.mutation<
      IApiResponse<null>,
      { assetId: number; employeeId: number }
    >({
      query: (body) => ({
        url: API_COMPANY_REASSIGN_ASSET(body.assetId),
        body: { assignedTo: body.employeeId },
        method: "PUT",
      }),
      invalidatesTags: ["companyGetSingleAssetTypeList"],
    }),
    companyDeleteAsset: builder.mutation<
      IApiResponse<null>,
      { assetId: number }
    >({
      query: (body) => ({
        url: API_COMPANY_DELETE_ASSET(body.assetId),
        method: "DELETE",
        body,
      }),
      invalidatesTags: [
        "companyGetSingleAssetTypeList",
        "companyGetAssetStats",
      ],
    }),
    companyDeleteAssetImage: builder.mutation<
      IApiResponse<null>,
      { imageId: number }
    >({
      query: (params) => ({
        url: API_COMPANY_DELETE_ASSET_IMAGE(params.imageId),
        method: "DELETE",
      }),
      invalidatesTags: ["companyGetAssetsImages"],
    }),
    companyGetAllConsumableAssets: builder.query<
      IApiPaginatedResponse<IConsumableAsset[]>,
      IApiGeneralParams & { consumableCategoryId?: number; unit?: string; branchId?: number }
    >({
      query: (params) => ({
        url: API_COMPANY_GET_ALL_CONSUMABLE_ASSETS,
        method: "GET",
        params,
      }),
      providesTags: ["companyGetAllConsumableAssets"],
    }),
    companyCreateConsumableAsset: builder.mutation<
      IApiResponse<void>,
      ICreateConsumableAssetPayload
    >({
      query: (body) => ({
        url: API_COMPANY_CREATE_CONSUMABLE_ASSET,
        method: "POST",
        body,
      }),
      invalidatesTags: ["companyGetAllConsumableAssets"],
    }),
    companyGetConsumableAssetById: builder.query<
      IApiResponse<IConsumableAsset>,
      { assetId: number }
    >({
      query: ({ assetId }) => ({
        url: API_COMPANY_GET_CONSUMABLE_ASSET_BY_ID(assetId),
        method: "GET",
      }),
    }),
    companyUpdateAssetConsumableAsset: builder.mutation<
      IApiResponse<null>,
      { assetId: number; payload: ICreateConsumableAssetPayload }
    >({
      query: ({ assetId, payload }) => ({
        url: API_COMPANY_UPDATE_CONSUMABLE_ASSET(assetId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["companyGetAllConsumableAssets"],
    }),
    companyDeleteConsmableAsset: builder.mutation<
      IApiResponse<void>,
      { assetId: number }
    >({
      query: ({ assetId }) => ({
        url: API_COMPANY_DELETE_CONSUMABLE_ASSET(assetId),
        method: "DELETE",
      }),
      invalidatesTags: ["companyGetAllConsumableAssets"],
    }),
    companyGetAllAssetRequests: builder.query<
      IApiPaginatedResponse<ISingleAssetRequest[]>,
      IApiGeneralParams
    >({
      query: (params) => ({
        url: API_COMPANY_GET_ALL_ASSET_REQUESTS,
        method: "GET",
        params,
      }),
      providesTags: ["companyGetAllAssetRequests"],
    }),
    companyGetAssetRequestStats: builder.query<
      IApiResponse<IAssetRequestStats>,
      void
    >({
      query: () => ({
        url: API_COMPANY_GET_ASSET_REQUEST_STATS,
        method: "GET",
      }),
      providesTags: ["companyAssetRequestStats"],
    }),
    companyApproveAssetRequest: builder.mutation<
      IApiResponse<null>,
      {
        requestId: number;
        notes?: string;
        assetIds: number[];
        oldAssetId?: number;
      }
    >({
      query: ({ requestId, notes, assetIds, oldAssetId }) => ({
        url: API_COMPANY_APPROVE_ASSET_REQUEST(requestId),
        method: "PUT",
        body: { notes, assetIds, oldAssetId },
      }),
      invalidatesTags: ["companyGetAllAssetRequests", "companyAssetRequestStats"],
    }),
    companyRejectAssetRequest: builder.mutation<
      IApiResponse<null>,
      { requestId: number; rejectionReason: string }
    >({
      query: ({ requestId, rejectionReason }) => ({
        url: API_COMPANY_REJECT_ASSET_REQUEST(requestId),
        method: "PUT",
        body: { rejectionReason },
      }),
      invalidatesTags: ["companyGetAllAssetRequests", "companyAssetRequestStats"],
    }),
    companyGetAssetRequestDetailsById: builder.query<
      IApiResponse<IAssetRequestData>,
      { requestId: number }
    >({
      query: ({ requestId }) => ({
        url: API_COMPANY_GET_ASSET_REQUEST_DETAILS_BY_ID(requestId),
        method: "GET",
      }),
    }),
    companyGetConsumableAssetCategory: builder.query<
      IApiResponse<IConsumableAssetCategory[]>,
      void
    >({
      query: () => ({
        url: API_COMPANY_GET_CONSUMABLE_ASSET_CATEGORY,
        method: "GET",
      }),
    }),
    companyGetConsumableUnitTypes: builder.query<
      IApiResponse<IConsumableUnitType[]>,
      void
    >({
      query: () => ({
        url: API_COMPANY_GET_CONSUMABLE_UNIT_TYPES,
        method: "GET",
      }),
    }),
    companyGetConsumableAssigneeList: builder.query<
      IApiPaginatedResponse<IConsumableAssignees[]>,
      IApiGeneralParams & { requestId: number }
    >({
      query: ({ requestId, ...params }) => ({
        url: API_COMPANY_GET_CONSUMABLE_ASSIGNEES(requestId),
        method: "GET",
        params,
      }),
    }),
  }),
});

export const {
  useCompanyGetAssetsInventoryQuery,
  useCompanyGetAssetStatsQuery,
  useCompanyGetAllAssetTypesQuery,
  useCompanyGetAllAssetBrandsQuery,
  useLazyCompanyGetAllAssetBrandsQuery,
  useCompanyGetSingleAssetTypeListQuery,
  useCompanyGetEmployeeAssignedAssetsQuery,
  useCompanyGetAssetDetailsByIdQuery,
  useCompanyGetAssetsImagesQuery,
  useCompanyGetAllConsumableAssetsQuery,
  useCompanyGetConsumableAssetByIdQuery,
  useCompanyRetireAssetMutation,
  useCompanyRestoreAssetMutation,
  useCompanyDeleteAssetMutation,
  useCompanyCreateNewAssetMutation,
  useCompanyDeleteAssetImageMutation,
  useCompanyUpdateAssetPrimaryImageMutation,
  useCompanyAssignAssetMutation,
  useCompanyUnassignAssetMutation,
  useCompanyUpdateAssetMutation,
  useCompanyCreateConsumableAssetMutation,
  useCompanyUpdateAssetConsumableAssetMutation,
  useCompanyDeleteConsmableAssetMutation,
  useCompanyGetAllAssetRequestsQuery,
  useCompanyGetAssetRequestStatsQuery,
  useCompanyApproveAssetRequestMutation,
  useCompanyRejectAssetRequestMutation,
  useCompanyGetAssetRequestDetailsByIdQuery,
  useCompanyGetConsumableAssetCategoryQuery,
  useCompanyGetConsumableUnitTypesQuery,
  useCompanyGetConsumableAssigneeListQuery,
  useCompanyGetEmployeeAssetHistoryQuery,
} = companyAssetsApi;
